/**
 * @file BlogPanel.tsx
 * @description Панель «Блог» (phase19.16 Р1 «Лента блога»; Р2 —
 * uiux-design-r2 §8, SB39, SB40, SB61, SB69, SB70, SB75): вкладки
 * [Лента][Статья][Лента RSS]. Лента — шапка ленты (встроенная или своя
 * блоком над лентой, решение 2026-10-11), страница «Блог», вид ленты и строка
 * «Блоков над лентой: N · под лентой: M — [Изменить на странице ›]» (экран
 * «Блог» в переключателе страниц); статья — шаблон статьи; RSS — лента и
 * подсказка про авторов. Часть BLOG — общая часть каркаса, вид ленты и
 * статьи публикуется вместе с видом сайта; блоки мест — через черновик места (Р3а).
 *
 * SB122 (uiux-settings-groups §5, шаг 6): «Лента» и «Статья» — группы-карточки
 * (`BlogFeedTab` — `panel="blog-feed"`, `BlogArticleTab` — `panel="blog-article"`);
 * строка мест над и под лентой — над группами, как в «Каталоге».
 */

import { readBlogOptions, type SiteBlogOptions } from "@chaiberi/shared";
import { useEffect, useState } from "react";

import { Tabs } from "../../ui/Tabs";
import { SaveIndicator } from "../SaveIndicator";
import { useSiteView } from "../site-view-context";
import { UnsavedGuard } from "../UnsavedGuard";
import { BlogArticleTab } from "./BlogArticleTab";
import { AuthorsHint, BlogRssCard } from "./BlogFeedCards";
import { BlogFeedTab } from "./BlogFeedTab";
import { BlogSlotsLine } from "./BlogSlotsLine";
import { useSiteDraftPart } from "../../../hooks/site-view/useSiteDraftParts";
import type { SiteViewDraft } from "../../../lib/site-view/types";

type BlogTab = "feed" | "article" | "rss";

const TABS = [
  { value: "feed" as const, label: "Лента" },
  { value: "article" as const, label: "Статья" },
  { value: "rss" as const, label: "Лента RSS" },
];

export function BlogPanel({ draft }: { draft: SiteViewDraft }) {
  const { siteUrl, preview } = useSiteView();
  const blog = useSiteDraftPart("blog");
  const [tab, setTab] = useState<BlogTab>("feed");
  const { focus } = preview;
  const options = readBlogOptions(blog.fields.options).options;
  const setOptions = (next: SiteBlogOptions) => blog.set("options", next);

  useEffect(() => {
    focus({ screen: "blog" });
  }, [focus]);

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <SaveIndicator state={blog} />
      <div className="shrink-0">
        <Tabs aria-label="Разделы блога" items={TABS} value={tab} onChange={setTab} />
      </div>
      {tab === "feed" ? (
        <>
          <BlogSlotsLine />
          <BlogFeedTab blog={blog} options={options} onChange={setOptions} />
        </>
      ) : null}
      {tab === "article" ? <BlogArticleTab options={options} onChange={setOptions} /> : null}
      {tab === "rss" ? (
        <>
          <BlogRssCard blog={blog} siteName={draft.brand.siteName} siteUrl={siteUrl} />
          <AuthorsHint />
        </>
      ) : null}
      <UnsavedGuard when={blog.status === "error" || blog.status === "conflict"} />
    </div>
  );
}
